"use client";

import React from "react";
import {
  Users,
  Globe,
  Mail,
  Heart,
  Shield,
  Star,
  MapPin,
  Calendar,
  Handshake,
  Phone,
  Download,
} from "lucide-react";
import {
  FaFacebookF,
  FaInstagram,
  FaTiktok,
  FaYoutube,
  FaWhatsapp,
  FaLinkedinIn,
} from "react-icons/fa";
import Image from "next/image";

const DIGITAL_OCEAN_SPACES_CDN =
  "https://lilylanka-public-prod.sgp1.cdn.digitaloceanspaces.com";

const logos = [
  `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/footer/about us/Dialog.jpeg`,
  // `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/footer/about us/IAE.jpeg`,
  `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/footer/about us/PeoplesInsurance.jpeg`,
  // `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/footer/about us/Police.jpeg`,
  `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/footer/about us/Sampath.jpeg`,
  // `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/footer/about us/Srilankan.jpeg`,
  `${DIGITAL_OCEAN_SPACES_CDN}/Site/images/footer/about us/STDA.jpeg`,
];

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white pt-16 md:pt-20">
      {/* Hero Section */}
      <div className="bg-white">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="flex justify-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-8 text-center relative inline-block">
              About LilyLanka
              <span className="block mx-auto mt-2 h-1 w-16 bg-purple-600 rounded-full"></span>
            </h1>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            {/* Left Content */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4 leading-tight">
                <span className="text-purple-600 text-3xl font-extrabold">
                  “
                </span>
                Where journeys begin with heart, and every path leads to
                discovery.
                <span className="text-purple-600 text-3xl font-extrabold">
                  ”
                </span>
              </h2>
              <p className="text-gray-600 dark:text-white text-sm leading-relaxed">
                At LilyLanka, we believe travel is more than movement—it's
                emotion, connection, and storytelling. We exist to help you
                experience Sri Lanka not just as a destination, but as a
                feeling.
              </p>
              <br />
              <p className="text-gray-600 dark:text-white text-sm leading-relaxed">
                From misty hill country mornings to golden coastal sunsets,
                every journey we craft is designed to create memories that stay
                with you long after you return home. Let us guide you through
                the island’s beauty with warmth, authenticity, and care.
              </p>
            </div>

            {/* Right Image */}
            <div className="relative h-64 lg:h-80 rounded-2xl overflow-hidden shadow-lg">
              <img
                src={`${DIGITAL_OCEAN_SPACES_CDN}/Site/images/footer/about us/traditiional art.jpg`}
                alt="Sri Lanka landscape"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Your All-in-One Section */}
      <div className="bg-gray-50 py-12">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Your All-in-One National Digital Tourism Platform
          </h2>
          <p className="text-gray-600 dark:text-white text-sm leading-relaxed">
            Welcome to LilyLanka—where the beauty, culture, and spirit of Sri
            Lanka come together in one seamless digital experience. From
            sun-kissed beaches and misty hilltops to ancient cities and vibrant
            local life, LilyLanka brings the entire island to your fingertips.
            Whether you're planning your first adventure or rediscovering your
            homeland, we're here to make every moment simple, safe, and
            unforgettable.
          </p>
        </div>
      </div>

      {/* Our Story */}
      <div className="bg-white py-12">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Story</h2>
          <p className="text-gray-600 dark:text-white text-sm leading-relaxed">
            LilyLanka began with a simple dream—to make exploring Sri Lanka
            easier for everyone. We saw how travelers often faced scattered
            information, unreliable bookings, and missed local treasures. So, we
            built a platform that connects it all—people to places, travelers to
            hosts, and curiosity to authentic discovery. Today, LilyLanka is
            becoming Sri Lanka's national digital tourism platform—uniting
            travelers, hotels, transport providers, and experience creators
            across the island.
          </p>
        </div>
      </div>

      {/* Our Mission */}
      <div className="bg-gray-50 py-12">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h2>
          <p className="text-gray-600 dark:text-white text-sm leading-relaxed">
            To connect the world with the real Sri Lanka through technology,
            trust, and a touch of island warmth. We aim to empower travelers to
            explore confidently while helping local communities and tourism
            partners grow in a more sustainable and connected way.
          </p>
        </div>
      </div>

      {/* What You Can Do */}
      <div className="bg-white py-12">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            What You Can Do with LilyLanka
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h3 className="font-semibold text-gray-900 mb-2 text-sm">
                Book your stay
              </h3>
              <p className="text-gray-600 dark:text-white text-xs leading-relaxed">
                From cozy homestays to world-class hotels, all verified and
                secure.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h3 className="font-semibold text-gray-900 mb-2 text-sm">
                Find your ride
              </h3>
              <p className="text-gray-600 dark:text-white text-xs leading-relaxed">
                Trusted transport solutions for every journey, from airport
                pickups to scenic drives.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h3 className="font-semibold text-gray-900 mb-2 text-sm">
                Discover experiences
              </h3>
              <p className="text-gray-600 dark:text-white text-xs leading-relaxed">
                From cultural tours and local food trails to adventure escapes
                and hidden gems.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-5">
              <h3 className="font-semibold text-gray-900 mb-2 text-sm">
                Support local
              </h3>
              <p className="text-gray-600 dark:text-white text-xs leading-relaxed">
                Every booking supports families, guides, and small businesses
                across the island.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-5 md:col-span-2">
              <h3 className="font-semibold text-gray-900 mb-2 text-sm">
                Travel smarter
              </h3>
              <p className="text-gray-600 dark:text-white text-xs leading-relaxed">
                Real reviews, transparent pricing, and 24/7 support for peace of
                mind.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* For Local Partners */}
      <div className="bg-gray-50 py-12">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            For Our Local Partners
          </h2>
          <p className="text-gray-600 dark:text-white text-sm leading-relaxed mb-4">
            LilyLanka is built not just for travelers—but for everyone who
            brings Sri Lanka to life. We help hotels, tour guides, drivers, and
            entrepreneurs go digital and reach travelers from all around the
            world.
          </p>
          <p className="text-gray-600 dark:text-white text-sm leading-relaxed mb-8">
            Together, we're building a stronger, more connected tourism
            community where opportunity flows both ways.
          </p>

          {/* Partner Logos */}
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
            {logos.map((logo, index) => (
              <div
                key={index}
                className="bg-white rounded-lg h-20 w-20 md:h-24 md:w-24 lg:h-30 lg:w-30 flex items-center justify-center border border-gray-200 flex-shrink-0"
              >
                <Image
                  src={logo}
                  alt={`Partner logo ${index + 1}`}
                  width={80}
                  height={80}
                  className="object-contain p-2"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Our Promise */}
      <div className="bg-white py-12">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Promise</h2>
          <p className="text-gray-600 dark:text-white text-sm leading-relaxed">
            We're here to make travel authentic, safe, and meaningful. Every
            destination, every partner, and every service is carefully chosen so
            you can explore with confidence and fall in love with the island
            again and again.
          </p>
        </div>
      </div>

      {/* Join the Journey */}
      <div className="bg-white  py-12">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-14">
            Join the Journey
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Travelers */}
            <div className="border-2 border-purple-300 rounded-xl p-8 bg-transparent">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Travelers
              </h3>
              <p className="text-gray-600 dark:text-white text-sm mb-6">
                Download the LilyLanka app and start exploring today.
              </p>

              <div className="flex flex-col gap-3 w-44">
                <a
                  href="https://apps.apple.com/lk/app/lily-lanka/id6760127534"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={`${DIGITAL_OCEAN_SPACES_CDN}/Site/images/footer/about us/apple.png`}
                    alt="App Store"
                    className="cursor-pointer"
                  />
                </a>

                <a
                  href="https://play.google.com/store/apps/details?id=com.lilylanka_mobile"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={`${DIGITAL_OCEAN_SPACES_CDN}/Site/images/footer/about us/google.png`}
                    alt="Google Play"
                    className="cursor-pointer"
                  />
                </a>
              </div>
            </div>

            {/* Connect */}
            <div className="border-2 border-purple-300 rounded-xl p-8 bg-transparent">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Connect
              </h3>
              <p className="text-gray-600 dark:text-white text-sm mb-6">
                Contact our team for media, community, or partnership
                opportunities.
              </p>

              <div className="flex items-center gap-5 text-gray-600">
                <a
                  href="https://www.facebook.com/share/15FprWU6HPt/?mibextid=wwXIfr"
                  target="_blank"
                >
                  <FaFacebookF className="icon" />
                </a>
                <a
                  href="https://www.instagram.com/lily__lanka?igsh=MTdjY2V2dTQ2MXFzdg%3D%3D&utm_source=qr"
                  target="_blank"
                >
                  <FaInstagram className="icon" />
                </a>
                <a
                  href="https://www.tiktok.com/@lily.lanka?_t=ZS-901fAQ9f4fF&_r=1"
                  target="_blank"
                >
                  <FaTiktok className="icon" />
                </a>
                <a href="https://www.youtube.com/@LilyLanka" target="_blank">
                  <FaYoutube className="icon" />
                </a>
                <a href="https://wa.me/94772090344" target="_blank">
                  <FaWhatsapp className="icon" />
                </a>
                {/* <a
                  href="https://www.linkedin.com/company/lilylanka"
                  target="_blank"
                >
                  <FaLinkedinIn className="icon" />
                </a> */}
              </div>
            </div>

            {/* Partners */}
            <div className=" border-2 border-purple-300 rounded-xl p-8 bg-transparent md:col-span-1">
              <h3 className="text-lg font-semibold  text-gray-900 mb-2">
                Partners
              </h3>
              <p className="text-gray-600 dark:text-white text-sm">
                Collaborate with us to grow your business and share your story.
              </p>
            </div>
          </div>
          <div className="mt-12 flex justify-center">
            <p className="text-2xl font-bold text-gray-900 dark:text-white text-center leading-relaxed max-w-3xl">
              <span className="text-purple-600 text-3xl font-extrabold">“</span>
              Because with LilyLanka, every journey begins
              <br />
              with love for our island.
              <span className="text-purple-600 text-3xl font-extrabold">”</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
